import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, Twitter, MessageCircle, Mail, Send } from "lucide-react";

/**
 * ShareSheet — channel-specific UTM-tagged share menu for the referral link.
 *
 * Each channel uses its own deep-link scheme + appends a UTM splitter so you
 * can later count "where did this signup come from" in your analytics.
 *
 * Channels (in display order):
 *   - Twitter / X            (web intent)
 *   - WhatsApp               (universal whatsapp:// fallback to wa.me)
 *   - Telegram               (t.me/share)
 *   - SMS                    (sms: scheme — opens iMessage / default SMS app)
 *   - Email                  (mailto:)
 *   - Copy link              (writes ?ref=CODE&utm_source=copy to clipboard)
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   refCode: string  (anonymous referral code OR email fallback)
 *   origin: string   (window.location.origin)
 */

// Pre-baked copy lines so each channel reads naturally on its native UI.
const SHARE_COPY = {
  twitter:  "Walking the path to Primordeia with Papaya, the white rabbit. Use my code so we both gain.",
  whatsapp: "I've been walking the path to Primordeia with Papaya, the white rabbit 🐇 — use my code so we both gain:",
  telegram: "Primordeia — walking the path with Papaya the white rabbit. Use my code so we both gain.",
  sms:      "Walking the path to Primordeia with Papaya, the white rabbit. Use my code so we both gain — ",
  email:    "I've been spending time on a strange and wonderful site called Primordeia — Papaya the white rabbit guides you through tiers of spiritual awakening. Each tier has its own song, lore, and a garment you earn by doing the inner work. Use my referral code so we both gain.\n\n",
};

const buildShareUrl = (origin, refCode, utmSource) => {
  const params = new URLSearchParams({
    ref: refCode,
    utm_source: utmSource,
    utm_medium: "referral",
    utm_campaign: "papaya_share",
  });
  return `${origin}/?${params.toString()}`;
};

const ShareSheet = ({ open, onClose, refCode, origin }) => {
  // Close on Escape
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!refCode) return null;

  const channels = [
    {
      key: "twitter",
      label: "Twitter / X",
      icon: <Twitter size={16} />,
      bg: "linear-gradient(135deg, #000 0%, #1d1d1d 100%)",
      onClick: () => {
        const url = buildShareUrl(origin, refCode, "twitter");
        const text = `${SHARE_COPY.twitter} ${url}`;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
                    "_blank", "noopener,noreferrer");
      },
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: <MessageCircle size={16} />,
      bg: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
      onClick: () => {
        const url = buildShareUrl(origin, refCode, "whatsapp");
        const text = `${SHARE_COPY.whatsapp} ${url}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`,
                    "_blank", "noopener,noreferrer");
      },
    },
    {
      key: "telegram",
      label: "Telegram",
      icon: <Send size={16} />,
      bg: "linear-gradient(135deg, #229ED9 0%, #1A7FA8 100%)",
      onClick: () => {
        const url = buildShareUrl(origin, refCode, "telegram");
        window.open(
          `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(SHARE_COPY.telegram)}`,
          "_blank", "noopener,noreferrer",
        );
      },
    },
    {
      key: "sms",
      label: "SMS / iMessage",
      icon: <MessageCircle size={16} />,
      bg: "linear-gradient(135deg, #34C759 0%, #1F8C3D 100%)",
      onClick: () => {
        const url = buildShareUrl(origin, refCode, "sms");
        const body = `${SHARE_COPY.sms}${url}`;
        // iOS uses ?body=, Android uses &body= — sms: scheme tolerates both via the ?
        window.location.href = `sms:?&body=${encodeURIComponent(body)}`;
      },
    },
    {
      key: "email",
      label: "Email",
      icon: <Mail size={16} />,
      bg: "linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)",
      onClick: () => {
        const url = buildShareUrl(origin, refCode, "email");
        const subject = "Found something for you — Primordeia";
        const body = `${SHARE_COPY.email}${url}`;
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      },
    },
    {
      key: "copy",
      label: "Copy link",
      icon: <Copy size={16} />,
      bg: "linear-gradient(135deg, #D4A574 0%, #8B6914 100%)",
      onClick: async () => {
        const url = buildShareUrl(origin, refCode, "copy");
        try {
          await navigator.clipboard.writeText(url);
          // Pop a tiny inline confirmation via the label
          const el = document.querySelector('[data-testid="share-channel-copy"]');
          if (el) {
            const orig = el.textContent;
            el.textContent = "✓ Copied!";
            setTimeout(() => { el.textContent = orig; }, 1800);
          }
        } catch (err) { console.warn("[ShareSheet] copy failed:", err?.message); }
      },
    },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[460] flex items-center justify-center px-4"
          style={{ background: "rgba(10, 5, 20, 0.65)", backdropFilter: "blur(6px)" }}
          onClick={onClose}
          data-testid="share-sheet"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-sm rounded-3xl p-6"
            style={{
              background: "linear-gradient(135deg, #1A1030 0%, #2A1845 60%, #1A1030 100%)",
              border: "1px solid rgba(212,165,116,0.4)",
              boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 text-white/70 hover:text-white"
              data-testid="share-sheet-close"
              aria-label="Close share menu"
            >
              <X size={18} />
            </button>

            <h2
              className="text-center text-xl mb-1"
              style={{
                fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                fontWeight: 700,
                color: "#D4A574",
                letterSpacing: "0.10em",
              }}
            >
              Share Primordeia
            </h2>
            <p
              className="text-center text-[10px] uppercase mb-5"
              style={{ color: "#8B7DA8", fontFamily: "'Cinzel', serif", letterSpacing: "0.28em" }}
            >
              your code · {refCode}
            </p>

            <div className="space-y-2">
              {channels.map((c) => (
                <motion.button
                  key={c.key}
                  type="button"
                  onClick={c.onClick}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-2xl text-sm font-semibold"
                  style={{
                    background: c.bg,
                    color: "#FFF",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
                  }}
                  data-testid={`share-channel-${c.key}`}
                >
                  <span className="flex items-center gap-2.5">{c.icon}{c.label}</span>
                  <span className="text-[10px] opacity-70 font-mono">utm_{c.key}</span>
                </motion.button>
              ))}
            </div>

            <p
              className="text-center text-[10px] italic mt-5"
              style={{ color: "#8B7DA8", fontFamily: "'Nunito', sans-serif" }}
            >
              Each channel is tagged so you can later see where your friends come from.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ShareSheet;
